/* ==========================================================================
   Xem trước tệp NGAY TRONG khung chat (mọi cỡ: thu nhỏ / mở rộng / toàn màn hình).
   · PDF   → <iframe> trỏ blob URL (trình xem PDF có sẵn của trình duyệt)
   · ảnh   → <img> · video → <video controls>
   · Word / Excel / PowerPoint → HTML do server chuyển (server/office.mjs) trong <iframe srcdoc sandbox> ⛔ script
   · HTML  → <iframe srcdoc sandbox> ⛔ script (giống khối HTML trong câu trả lời)
   Luôn có "Tải về" + "Mở tab mới" — trang chủ có CSP chặn blob/iframe thì người dùng vẫn lấy được tệp.
   ========================================================================== */
import { el } from "./dom.ts";
import { icon } from "./icons.ts";

export interface PreviewSource {
  name: string;
  type: string;
  /** PDF / ảnh / video: nội dung đã tải (có xác thực) */
  blob?: Blob;
  /** Office / HTML: trang HTML đã dựng */
  html?: string;
  /** Office: tệp GỐC (docx/xlsx/pptx) — "Tải về" lưu tệp này thay vì trang HTML xem trước */
  file?: Blob;
  /** "Tải về" lấy tệp này thay vì nội dung đang xem (vd xem bản HTML của báo cáo, tải bản PDF) */
  downloadUrl?: string;
  downloadName?: string;
}

const msg = (cls: string, text: string): HTMLElement => {
  const d = el("div", cls);
  d.textContent = text;
  return d;
};

/* chặn mọi thứ trừ ảnh nhúng + style nội dòng — tài liệu khách ⛔ chạy được gì */
const SANDBOX_CSP = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data: blob:; style-src 'unsafe-inline'; font-src data:"><style>body{max-width:920px;margin-left:auto!important;margin-right:auto!important;padding:0 12px}</style>`;

export class FilePreview {
  private readonly box: HTMLDivElement;
  private readonly title: HTMLSpanElement;
  private readonly body: HTMLDivElement;
  private readonly dl: HTMLButtonElement;
  private readonly tab: HTMLButtonElement;
  private url: string | null = null;
  private current: PreviewSource | null = null;
  private restore: HTMLElement | null = null;
  /** Widget gắn: tải một URL về máy (fetch + blob, có báo tiến độ). */
  onDownloadUrl: ((url: string, name: string) => void) | null = null;

  constructor(host: HTMLElement) {
    this.box = el("div", "apx-preview", { role: "dialog", "aria-modal": "true", "aria-label": "Xem trước tệp", hidden: "" }) as HTMLDivElement;
    const bar = el("div", "apx-preview-bar");
    this.title = el("span", "apx-preview-title") as HTMLSpanElement;
    this.tab = el("button", "apx-headbtn", { type: "button", title: "Mở trong tab mới", "aria-label": "Mở trong tab mới" }) as HTMLButtonElement;
    this.tab.innerHTML = icon("external", 16);
    this.dl = el("button", "apx-headbtn", { type: "button", title: "Tải về", "aria-label": "Tải về" }) as HTMLButtonElement;
    this.dl.innerHTML = icon("download", 16);
    const close = el("button", "apx-headbtn", { type: "button", title: "Đóng", "aria-label": "Đóng xem trước" });
    close.innerHTML = icon("close", 16);
    bar.append(this.title, this.tab, this.dl, close);
    this.body = el("div", "apx-preview-body") as HTMLDivElement;
    this.box.append(bar, this.body);
    host.append(this.box);
    close.addEventListener("click", () => this.close());
    this.dl.addEventListener("click", () => this.save());
    this.tab.addEventListener("click", () => this.openTab());
    this.box.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Escape") {
        e.stopPropagation();
        this.close();
      }
    });
  }

  get isOpen(): boolean {
    return !this.box.hidden;
  }

  /** Hiện khung chờ ngay khi bấm (tải tệp có thể mất vài giây). */
  loading(name: string): void {
    this.reset();
    this.title.textContent = name;
    this.body.replaceChildren(msg("apx-preview-msg", "Đang mở " + name + "…"));
    this.show();
  }

  error(name: string, message: string): void {
    this.title.textContent = name;
    this.body.replaceChildren(msg("apx-preview-msg apx-preview-err", message));
    this.dl.disabled = !this.current?.blob;
    this.show();
  }

  open(src: PreviewSource): void {
    this.reset();
    this.current = src;
    this.title.textContent = src.name;
    let view: HTMLElement;
    if (src.blob) {
      this.url = URL.createObjectURL(src.blob);
      const t = src.type || src.blob.type;
      if (t.startsWith("image/")) view = el("img", "apx-preview-img", { src: this.url, alt: src.name });
      else if (t.startsWith("video/")) view = el("video", "apx-preview-video", { src: this.url, controls: "", preload: "metadata", playsinline: "" });
      else view = el("iframe", "apx-preview-frame", { src: this.url, title: src.name });
    } else {
      const doc = String(src.html || "").replace(/<head>/i, "<head>" + SANDBOX_CSP);
      view = el("iframe", "apx-preview-frame apx-preview-doc", { sandbox: "", srcdoc: /<head>/i.test(doc) ? doc : SANDBOX_CSP + doc, title: src.name });
    }
    this.body.replaceChildren(view);
    this.dl.disabled = !src.blob && !src.html && !src.downloadUrl && !src.file;
    this.dl.title = src.downloadUrl ? "Tải PDF" : "Tải về";
    this.tab.hidden = !src.blob;
    this.show();
  }

  close(): void {
    this.box.hidden = true;
    this.reset();
    if (this.restore?.isConnected) this.restore.focus();
    this.restore = null;
  }

  destroy(): void {
    this.reset();
    this.box.remove();
  }

  private show(): void {
    if (this.box.hidden) this.restore = (this.box.getRootNode() as ShadowRoot).activeElement as HTMLElement | null;
    this.box.hidden = false;
    (this.box.querySelector(".apx-headbtn:last-child") as HTMLElement | null)?.focus();
  }

  private reset(): void {
    this.body.replaceChildren();
    if (this.url) URL.revokeObjectURL(this.url);
    this.url = null;
    this.current = null;
    this.dl.disabled = true;
    this.tab.hidden = true;
  }

  /** Tải về: blob gốc; với HTML (Office đã chuyển) thì lưu trang HTML. */
  private save(): void {
    const c = this.current;
    if (!c) return;
    if (c.downloadUrl && this.onDownloadUrl) return this.onDownloadUrl(c.downloadUrl, c.downloadName || c.name);
    const blob = c.file || c.blob || new Blob([c.html || ""], { type: "text/html" });
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = c.file || c.blob ? c.name : c.name.replace(/\.[^.]+$/, "") + ".html";
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 4000);
  }

  private openTab(): void {
    if (this.url) window.open(this.url, "_blank", "noopener");
  }
}

/** Biểu tượng theo loại tệp (chip đính kèm). */
export function fileIcon(type: string): string {
  if (type.startsWith("image/")) return "image";
  if (type.startsWith("video/")) return "play";
  return "file";
}

/** Nhãn loại ngắn cho chip: PDF, DOCX, XLSX, PPTX, MP4… */
export function fileKind(type: string, name: string): string {
  const ext = (name.split(".").pop() || "").toUpperCase();
  if (type === "application/pdf") return "PDF";
  if (/wordprocessingml/.test(type)) return "DOCX";
  if (/spreadsheetml/.test(type)) return "XLSX";
  if (/presentationml/.test(type)) return "PPTX";
  return ext.length <= 4 ? ext : type.split("/").pop()!.toUpperCase();
}
